import {
  ContractReference,
  ContractReferenceType
} from 'Cube/domain/cube.domain.types'
import { match } from 'ts-pattern'
import { CONTRACT_REFERENCE_TYPES } from './contractReferences.constants'

/**
 * A reference along with where it sits in the phase's full list. References
 * have no id of their own, and a phase can hold several `OTHER` ones, so edits
 * find the reference by its position.
 */
export type IndexedContractReference = {
  reference: ContractReference
  index: number
}

const WEB_PROTOCOLS = ['http:', 'https:']

/**
 * The link to open for a reference, if it has one we can follow. An `OTHER`
 * reference's link is whatever the user typed, so anything that isn't an
 * http(s) address stays stored but is never opened.
 */
export const getWebUrl = (url: string | null): string | undefined => {
  const parsedUrl = url ? URL.parse(url) : null

  return parsedUrl && WEB_PROTOCOLS.includes(parsedUrl.protocol)
    ? parsedUrl.href
    : undefined
}

/**
 * NetSuite references are only shown while NetSuite is connected. Hidden ones
 * stay in the phase's list, so saving after another edit doesn't drop them.
 */
export const getVisibleContractReferences = ({
  references,
  isNetsuiteAvailable
}: {
  references: Array<ContractReference>
  isNetsuiteAvailable: boolean
}): Array<IndexedContractReference> =>
  references
    .map((reference, index) => ({ reference, index }))
    .filter(
      ({ reference }) => isNetsuiteAvailable || reference.type !== 'NETSUITE'
    )
    .sort(
      (a, b) =>
        CONTRACT_REFERENCE_TYPES.indexOf(a.reference.type) -
        CONTRACT_REFERENCE_TYPES.indexOf(b.reference.type)
    )

/**
 * A phase holds at most one quote, opportunity and NetSuite tag, but any number
 * of other references.
 */
export const getAddableContractReferenceTypes = ({
  references,
  isNetsuiteAvailable,
  hasCustomer
}: {
  references: Array<ContractReference>
  isNetsuiteAvailable: boolean
  /** Quotes are picked from the customer's own, so there must be one. */
  hasCustomer: boolean
}): Array<ContractReferenceType> =>
  CONTRACT_REFERENCE_TYPES.filter(type => {
    const isAlreadyOnPhase = references.some(
      reference => reference.type === type
    )
    const isUnavailable = match(type)
      .with('NETSUITE', () => !isNetsuiteAvailable)
      .with('SEQUENCE_QUOTE', () => !hasCustomer)
      .otherwise(() => false)

    return !isUnavailable && (type === 'OTHER' || !isAlreadyOnPhase)
  })

/**
 * Two `OTHER` references can't share both a name and a link. The API refuses
 * the save, so we catch it in the dialog instead.
 */
export const isDuplicateOtherReference = ({
  references,
  candidate,
  ignoreIndex
}: {
  references: Array<ContractReference>
  candidate: { value: string; url: string | null }
  /** The reference being changed, which can't clash with itself. */
  ignoreIndex?: number
}): boolean =>
  references.some(
    (reference, index) =>
      index !== ignoreIndex &&
      reference.type === 'OTHER' &&
      reference.value === candidate.value &&
      (reference.url ?? null) === candidate.url
  )

/** The reference being changed, or none when a dialog is adding one. */
export const getReferenceAt = (
  references: Array<ContractReference>,
  index: number | undefined
): ContractReference | undefined =>
  index === undefined ? undefined : references[index]

/** The same dialog adds or changes a reference, so only its copy differs. */
export const getReferenceModalCopy = ({
  isChanging,
  noun
}: {
  isChanging: boolean
  noun: string
}): { title: string; submitButtonText: string } =>
  isChanging
    ? { title: `Change ${noun}`, submitButtonText: 'Save' }
    : { title: `Add ${noun}`, submitButtonText: 'Add' }
